import { useState, useEffect } from "react";
import { projectAuth } from "../firebase/config";
import { useAuthContext } from "./useAuthContext";
import { signInWithEmailAndPassword, getRedirectResult } from "firebase/auth";
import { provisionStatus } from "../firebase/provision";
import { signInWithSso, isVerifiedUser } from "../firebase/sso";
import { useSsoLookup } from "./useSsoLookup";

const ACCOUNT_EXISTS =
  "An account with this email already exists. Contact support@numetrics.org and we'll connect it to your district sign-in.";

const authErrorMessage = (err) => {
  const code = (err && err.code) || "auth/unknown";
  return code === "auth/user-not-found" || code === "auth/wrong-password"
    ? "incorrect email or password."
    : code.split("auth/")[1] || code;
};

export const useLogin = () => {
  const [isCancelled, setIsCancelled] = useState(false);
  const [error, setError] = useState(null);
  const [isPending, setIsPending] = useState(false);
  const { ssoProviderId, checkEmail } = useSsoLookup();
  const { dispatch } = useAuthContext();

  const continueWithSso = async (providerId) => {
    setError(null);
    setIsPending(true);
    let res;
    try {
      res = await signInWithSso(providerId); // popup; resolves with the credential
    } catch (err) {
      const code = (err && err.code) || "";
      if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request"
      ) {
        setIsPending(false); // they closed the window; nothing to report
        return false;
      }
      setError(
        code === "auth/account-exists-with-different-credential"
          ? ACCOUNT_EXISTS
          : code === "auth/popup-blocked"
          ? "Your browser blocked the sign-in window. Allow pop-ups for this site and try again."
          : authErrorMessage(err)
      );
      setIsPending(false);
      return false;
    }
    return admit(res.user, res.user.email);
  };

  // Everything after a successful sign-in, shared by the password path and
  // the SSO redirect return.
  const admit = async (user, email) => {
    // Provision gate — the sign-in list is the sign-up list (see
    // src/firebase/provision.js; AuthContext runs the same check on session
    // restore). signOut never throws here: the session is already persisted,
    // and a rejection must not skip the LOGOUT dispatch below.
    const status = await provisionStatus(user.email || email);
    if (status !== "listed") {
      await projectAuth.signOut().catch(() => {});
      dispatch({ type: "LOGOUT" });
      setError(
        status === "unknown"
          ? "Could not verify access. Please try again."
          : "This account is not authorized for the CTE Dashboard."
      );
      setIsPending(false);
      return false;
    }

    dispatch({ type: "LOGIN", payload: user });
    if (!isCancelled) {
      setIsPending(false);
      setError(null);
      // Return true so the calling code knows it was successful
      return true;
    }
  };

  const login = async (email, password) => {
    setError(null);
    setIsPending(true);

    const { status, ssoProviderId: providerId } = await checkEmail(email);
    if (providerId) {
      return continueWithSso(providerId);
    }
    // Never send a password without knowing the email isn't an SSO domain.
    if (status === "unknown") {
      setError("Could not verify access. Please try again.");
      setIsPending(false);
      return false;
    }
    // The password field was hidden for an SSO email that has since changed;
    // it is showing again now, so wait for the password.
    if (!password) {
      setIsPending(false);
      return false;
    }

    try {
      // login
      const res = await signInWithEmailAndPassword(
        projectAuth,
        email,
        password
      );

      // dispatch login action

      if (res.user && !isVerifiedUser(res.user)) {
        setError("Email has not been verified.");
        await projectAuth.signOut().catch(() => {});
        dispatch({ type: "LOGOUT" });
        setIsPending(false);
        return;
      }

      return await admit(res.user, email);
    } catch (err) {
      if (!isCancelled) {
        setError(authErrorMessage(err));

        setIsPending(false);
        // Return false (or throw) to indicate it failed
        return false;
      }
    }
  };

  // Call once on page load. AuthContext's listener has already restored the
  // returned user as the session; this runs it through the same admit step
  // as a password login so the page can route it the same way.
  const completeSsoRedirect = async () => {
    let res;
    try {
      res = await getRedirectResult(projectAuth);
    } catch (err) {
      setError(
        err && err.code === "auth/account-exists-with-different-credential"
          ? ACCOUNT_EXISTS
          : authErrorMessage(err)
      );
      return false;
    }
    if (!res || !res.user) return false;
    setIsPending(true);
    return admit(res.user, res.user.email);
  };

  useEffect(() => {
    return () => setIsCancelled(true);
  }, []);

  return {
    login,
    isPending,
    error,
    ssoProviderId,
    checkEmail,
    continueWithSso,
    completeSsoRedirect,
  };
};
